import {IconButton} from '@wandb/components';
import {debounce} from 'lodash';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {useIsInLoadedReportContext} from '../pages/ReportPage/LoadedReportContext';
import {SearchHistory} from './PanelBank/search/SearchHistory';
import {trackSearchHistory} from './PanelBank/search/searchHistoryEvents';
import {
  type OptionTypeWithRegex,
  type RunSearchHistoryItem,
} from './PanelBank/types';

interface RunsSearchBarProps {
  isGroupSearch: boolean;
  search: {
    query: string;
    isRegex: boolean;
  };
  setSearch(search: string, isRegex: boolean): void;
  searchHistory?: RunSearchHistoryItem[];
  onAddToHistory?(query: string, regex?: boolean): void;
}

const RunsSearchBarComp: React.FC<RunsSearchBarProps> = ({
  isGroupSearch,
  search: propsSearch,
  setSearch,
  searchHistory,
  onAddToHistory,
}) => {
  const isInReport = useIsInLoadedReportContext();
  const [inputValue, setInputValue] = useState(propsSearch.query);
  const [regex, setRegex] = useState(propsSearch.isRegex);

  const [selectedOption, setSelectedOption] =
    useState<OptionTypeWithRegex | null>(null);

  /**
   * Used for analytics tracking. We want to log "Runs searched" event
   * on blur, but only if the value has actually changed since focused.
   */
  const [valueAtFocus, setValueAtFocus] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value !== propsSearch.query || regex !== propsSearch.isRegex) {
          setSearch(value, regex);
        }
      }, 500),
    [propsSearch, regex, setSearch]
  );

  useEffect(() => {
    const input = inputValue.trim();
    debouncedSearch(input);
  }, [inputValue, debouncedSearch, propsSearch, regex]);

  const toggleRegex = useCallback(
    event => {
      setRegex(prev => {
        const next = !prev;
        trackSearchHistory({
          action: 'Regex toggled',
          location: isInReport
            ? 'report run selector'
            : 'workspace run selector',
          regex: next ? 'on' : 'off',
        });
        return next;
      });
      event.stopPropagation();
    },
    [isInReport]
  );

  const onFocus = () => {
    if (valueAtFocus !== inputValue) {
      setValueAtFocus(inputValue);
    }
  };

  const onBlur = (inputValue: string) => {
    if (inputValue !== valueAtFocus) {
      trackSearchHistory({
        action: 'Item added',
        location: isInReport ? 'report run selector' : 'workspace run selector',
        regex: regex ? 'on' : 'off',
      });
      setValueAtFocus('');
    }
    // when to add to search history is a bit tricky - we chose blur since
    // it is a reasonable proxy for "the user finished editing the search",
    // and trying to save after X seconds of pause could be wrong if a
    // user stopped to think of something while typing a search.
    if (!inputValue) {
      return;
    }
    onAddToHistory?.(inputValue, regex);
    trackSearchHistory({
      action: 'Item selected',
      location: isInReport ? 'report run selector' : 'workspace run selector',
      regex: regex ? 'on' : 'off',
    });
  };

  const onOptionChange = (newValue: OptionTypeWithRegex | string) => {
    const newOption =
      typeof newValue === 'string'
        ? {
            value: newValue,
            label: newValue,
            isRegex: regex,
          }
        : newValue;
    setSelectedOption(newOption);
    setRegex(newOption.isRegex);
  };

  const customActionButton = (
    <IconButton
      isActive={regex}
      aria-label="Toggle regex matching"
      onClick={toggleRegex}
      icon="regex"
      size="sm"
      variant="secondary"
      tooltip={{children: `Regex matching is ${regex ? 'on' : 'off'}`}}
    />
  );

  return (
    <SearchHistory
      searchHistory={searchHistory ?? []}
      onOptionChange={onOptionChange}
      isCollapsible
      selectedOption={selectedOption}
      setSelectedOption={setSelectedOption}
      onFocus={onFocus}
      onBlur={onBlur}
      minWidth={isInReport ? 200 : 150}
      setInputValue={setInputValue}
      inputValue={inputValue ?? ''}
      placeholderText={`Search ${isGroupSearch ? 'groups' : 'runs'}`}
      maxItems={10}
      customActionButton={customActionButton}
    />
  );
};

export const RunsSearchBar = memo(RunsSearchBarComp);
